import { useRouteError, isRouteErrorResponse, useNavigate } from "react-router-dom";

const C = {
  gold: "#C8860A",
  goldPale: "#FDF3DC",
  brown: "#6B3F1A",
  brownDark: "#3D1F08",
  cream: "#FBF5E6",
  bg: "#F7F0E3",
  border: "rgba(139,90,43,0.14)",
  textSoft: "#8C6B4A",
  green: "#2D6A4F",
  red: "#C0392B",
  redPale: "#FDECEA",
} as const;

export default function ErrorPage() {
  const error = useRouteError();
  const navigate = useNavigate();

  const is404 =
    isRouteErrorResponse(error) && error.status === 404;

  return (
    <div
      style={{
        minHeight: "100vh",
        background: C.bg,
        fontFamily: "'DM Sans', sans-serif",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
      }}
    >
      {/* Decorative blobs */}
      <div
        style={{
          position: "fixed",
          top: -120,
          right: -120,
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(200,134,10,0.12) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "fixed",
          bottom: -100,
          left: -100,
          width: 360,
          height: 360,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(107,63,26,0.08) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      <div style={{ textAlign: "center", maxWidth: 480, position: "relative" }}>
        {/* Icon ring */}
        <div
          style={{
            width: 100,
            height: 100,
            borderRadius: "50%",
            background: is404 ? C.goldPale : C.redPale,
            border: `3px solid ${is404 ? C.gold : C.red}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 28px",
            fontSize: 44,
            boxShadow: `0 8px 24px ${is404 ? "rgba(200,134,10,0.15)" : "rgba(192,57,43,0.15)"}`,
          }}
        >
          {is404 ? "🌾" : "🫙"}
        </div>

        {/* Status code */}
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 2,
            textTransform: "uppercase",
            color: is404 ? C.gold : C.red,
            marginBottom: 12,
          }}
        >
          {is404 ? "404 · Page Not Found" : "Something went wrong"}
        </div>

        {/* Heading */}
        <h1
          style={{
            fontFamily: "'Yatra One', cursive, 'DM Sans', sans-serif",
            fontSize: 36,
            fontWeight: 700,
            color: C.brownDark,
            margin: "0 0 14px",
            lineHeight: 1.2,
          }}
        >
          {is404 ? "Lost in the fields?" : "Oops! Hit a snag"}
        </h1>

        {/* Description */}
        <p
          style={{
            fontSize: 16,
            color: C.textSoft,
            lineHeight: 1.6,
            margin: "0 0 36px",
          }}
        >
          {is404
            ? "The page you're looking for has been moved, deleted, or never existed. Let's get you back on track."
            : "Something unexpected happened on our end. Don't worry — your data is safe. Please try again or head back to the dashboard."}
        </p>

        {/* Action buttons */}
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              padding: "11px 24px",
              borderRadius: 9,
              border: `1.5px solid ${C.border}`,
              background: C.cream,
              color: C.brown,
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = C.goldPale;
              (e.currentTarget as HTMLButtonElement).style.borderColor = C.gold;
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = C.cream;
              (e.currentTarget as HTMLButtonElement).style.borderColor = C.border;
            }}
          >
            ← Go Back
          </button>
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              padding: "11px 24px",
              borderRadius: 9,
              border: "none",
              background: C.brownDark,
              color: "#fff",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              transition: "background 0.2s",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = C.brown;
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = C.brownDark;
            }}
          >
            🏠 Go to Dashboard
          </button>
        </div>

        {/* Brand footer */}
        <div
          style={{
            marginTop: 48,
            paddingTop: 20,
            borderTop: `1px solid ${C.border}`,
            fontSize: 12,
            color: C.textSoft,
          }}
        >
          🌿 <strong style={{ color: C.brown }}>JaggeryBook</strong> — Built for jaggery manufacturers
        </div>
      </div>
    </div>
  );
}
